export type CatalogueResolveResult = { ownCode: string; state: 'NOT_FOUND' | 'MATCHED' | 'AMBIGUOUS'; productIds: string[] };

export function normalizeOwnCode(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleUpperCase('en-GB');
}

function localRoot(value: string): string {
  if (!value.startsWith('/') || value.startsWith('//') || /[\\?#\u0000-\u001f\u007f]/.test(value)) throw new Error('invalid local API root');
  return value.replace(/\/$/, '');
}

/** Validates the complete resolver response before any catalogue identity is trusted. */
export function parseCatalogueResolveResponse(value: unknown, requestedCodes: readonly string[]): CatalogueResolveResult[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('invalid resolver response');
  const body = value as Record<string, unknown>;
  if (Object.keys(body).sort().join(',') !== 'apiVersion,results' || body.apiVersion !== 2 || !Array.isArray(body.results)) throw new Error('invalid resolver response');
  const requested = new Map<string, string>();
  for (const code of requestedCodes) {
    const normalized = normalizeOwnCode(code);
    if (!normalized || requested.has(normalized)) throw new Error('invalid resolver request');
    requested.set(normalized, code);
  }
  if (body.results.length !== requested.size) throw new Error('incomplete resolver response');
  const seen = new Set<string>();
  const results = body.results.map((raw): CatalogueResolveResult => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('invalid resolver result');
    const result = raw as Record<string, unknown>;
    if (Object.keys(result).sort().join(',') !== 'ownCode,productIds,state'
      || typeof result.ownCode !== 'string' || result.ownCode.trim() !== result.ownCode || /[\u0000-\u001f\u007f]/.test(result.ownCode)
      || !Array.isArray(result.productIds) || !result.productIds.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 180 && id.trim() === id && !/[\u0000-\u001f\u007f]/.test(id)))
      throw new Error('invalid resolver result');
    const key = normalizeOwnCode(result.ownCode);
    if (!requested.has(key) || seen.has(key)) throw new Error('unexpected or duplicate resolver result');
    seen.add(key);
    const ids = result.productIds as string[];
    const uniqueIds = new Set(ids);
    if (uniqueIds.size !== ids.length) throw new Error('duplicate product id');
    const expectedState = ids.length === 0 ? 'NOT_FOUND' : ids.length === 1 ? 'MATCHED' : 'AMBIGUOUS';
    if (result.state !== expectedState) throw new Error('inconsistent resolver state');
    return { ownCode: result.ownCode, state: expectedState, productIds: [...ids] };
  });
  if (seen.size !== requested.size) throw new Error('incomplete resolver response');
  return results;
}

export async function resolveOwnCodes(fetcher: typeof fetch, apiRoot: string, ownCodes: readonly string[]): Promise<CatalogueResolveResult[]> {
  if (ownCodes.length < 1 || ownCodes.length > 50 || ownCodes.some((code) => !code || code.length > 100 || code.trim() !== code || /[\u0000-\u001f\u007f]/.test(code)))
    throw new Error('invalid own-code request');
  const url = `${localRoot(apiRoot)}/solar/v2/catalogue/resolve`;
  const response = await fetcher(url, {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ apiVersion: 2, ownCodes }),
  });
  if (!response.ok) throw new Error('catalogue lookup unavailable');
  return parseCatalogueResolveResponse(await response.json() as unknown, ownCodes);
}
